import { mapGetters } from 'vuex'
import notifyOverride from '../components/generic/VxNotification'
import _ from 'lodash'

function getComponentStack(component) {
    const findParents = function (child, parents) {
        parents = parents || []
        if (!child) {
            return parents
        }
        var name = child.$options && child.$options.name
        var backup = child.$vnode && child.$vnode.tag
        parents.push(name || backup || 'unknown-component')
        return findParents(child.$parent, parents)
    }

    return ' -> ' + findParents(component).join('\n\t -> ')
}

async function showReportableError(context, { title, description, error }) {
    try {
        context.loading = true

        // var binaryImage = await document.body.toImage()
        // var stackTrace = getComponentStack(context)
        // var route = context.$route

        context.notifyError(
            title,
            `${description}`
            // null,
            // function (e) {
            //     if (e.target.localName != 'a') {
            //         return
            //     }
            //     context.$store.commit('bugs/OPEN_REPORT', {
            //         error,
            //         route,
            //         binaryImage,
            //         stackTrace
            //     })
            // }
        )
    } finally {
        context.loading = false
    }
}

var mixins = {
    data() {
        return {
            loading: false,
            response_interceptor: null
        }
    },
    watch: {
        loading(val) {
            var target = this.$el
            // comment node, usually occurs when manual dom manipulation
            // occurs, as the component is moved to a new node
            if (this.$el.nodeType == 8) {
                if (!this.$children.any()) {
                    // it is probably a teleported component to the dom root
                    target = document.body
                } else {
                    target = this.$children.first().$el
                }
            }
            if (val) {
                this.startLoad(target)
            } else {
                this.endLoad(target)
            }
        }
    },
    methods: {
        async notifyFrontendError(error) {
            if (!error) {
                return
            }
            console.error(error)
            const title = 'Unexpected Error'
            const description =
                'An unknown error occurred on the frontend, please try again. If the problem persists, please report the issue.'
            showReportableError(this, { title, description, error })
        },
        async notifyServerError(error) {
            if (!error) {
                return
            }

            if (!error.response) {
                await this.notifyFrontendError(error)
                return
            }
            console.error(error)
            var description = error.response.data.Message
            var code = error.response.status

            // these are not server errors, more likely user errors aside from 503
            if (code < 500) {
                this.notifyError(error.response.statusText, description, 10000)
                return
            }

            if (code == 503) {
                this.notifyError(
                    'Server Unavailable',
                    'Backend is down, this should only be temporary, please try again in a few minutes.',
                    10000
                )
                return
            }

            var title = `${code} - ${error.response.statusText}`
            showReportableError(this, { title, description, error })
        },
        userHasRole(roles) {
            return this.$store.getters['auth/has_role'](roles)
        },
        delayFor(millis) {
            return new Promise((resolve) => {
                setTimeout(resolve, millis)
            })
        },
        deepCopy(obj) {
            if (obj == null) {
                return null
            }
            return _.cloneDeep(obj)
        },
        startLoad(el) {
            if (el) {
                this.$vs.loading({ container: el })
            } else {
                this.$vs.loading()
            }
        },
        watch(var_to_watch) {
            return new Promise(
                function (resolve) {
                    const watcher = this.$watch(
                        var_to_watch,
                        function (newVal) {
                            resolve(newVal)
                            watcher()
                        }.bind(this)
                    )
                }.bind(this)
            )
        },
        async validate() {
            var results = await Promise.all(this.validators.map((v) => v.validate()))
            return results.all((v) => v === true)
        },
        async endLoad(el) {
            // vuesax bug, if close happens too soon after start it never closes the loader. Possibly
            // due to the virtual dom not being pushed to live yet, despite nextTick() not working
            await this.delayFor(250)
            if (el) {
                this.$vs.loading.close(el)
            } else {
                this.$vs.loading.close()
            }
        },
        notifyError(title, text, delay, onClick) {
            this.$vs.notify({
                title: title,
                text: text,
                iconPack: 'feather',
                icon: 'icon-alert-circle',
                color: 'danger',
                fixed: !delay,
                time: delay,
                click: onClick
            })
        },
        notifySuccess(title, text, delay, onClick) {
            this.$vs.notify({
                title: title,
                text: text,
                iconPack: 'feather',
                icon: 'icon-check-triangle',
                color: 'success',
                fixed: !delay,
                time: delay,
                click: onClick
            })
        },
        notifyWarning(title, text, delay, onClick) {
            this.$vs.notify({
                title: title,
                text: text,
                iconPack: 'feather',
                icon: 'icon-check-square',
                color: 'warning',
                fixed: !delay,
                time: delay,
                click: onClick
            })
        },
        confirm(options) {
            return new Promise((resolve, reject) => {
                this.$vs.dialog({
                    color: 'danger',
                    title: 'Confirm',
                    ...options, // Override the above paramters, but not the below ones
                    type: 'confirm',
                    accept: () => {
                        try {
                            if (options.accept && typeof options.accept === 'function') {
                                options.accept()
                            }
                        } finally {
                            resolve(true)
                        }
                    },
                    cancel: () => resolve(false)
                })
            })
        },
        toggleSideBar() {
            this.$emit('toggle-side-bar')
        },
        resetValidation() {
            this.$validator.reset()
        },
        shake(timeout) {
            this.$el.classList.add('shake')
            setTimeout(() => {
                this.$el.classList.remove('shake')
            }, timeout || 200)
        },
        rebound() {
            this.$el.classList.add('rebound')
            setTimeout(() => {
                this.$el.classList.remove('rebound')
            }, 100)
        }
    },
    computed: {
        ...mapGetters({
            authenticated: 'auth/is_authenticated',
            user: 'user/profile'
        }),
        allFieldsValid() {
            return this.validationFields.all((el) => el.flags.valid)
        },
        isFormDirty() {
            return this.validationFields.any((el) => el.flags.dirty)
        },
        validators() {
            if (!this.$validator) {
                return []
            }
            if (this.$validator.fields.length === 1) {
                return [this.$validator]
            }
            return this.$children.selectMany((c) => c.validators)
        },
        validationFields() {
            return this.validators.selectMany((v) => {
                if (v.fields.length == 0) {
                    return []
                }
                return v.fields.items.where((f) => Object.keys(f.rules).any())
            })
        }
    },
    created() {
        this.$vs.notify = notifyOverride.vsfunction
    }
}

export default mixins
